/*
 * Copyright (c) 2026 Paxia AI Pty Ltd. All rights reserved.
 * This software is proprietary and confidential.
 * No part of this codebase may be copied, modified, published, distributed, sublicensed, or used without prior written permission from Paxia AI Pty Ltd.
 */

import React, { useEffect, useMemo, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { getAIUsageAnalytics, getAIUsageDrilldown } from "@/api/billing";
import {
  BarChart,
  Bar,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from "recharts";
import { BarChart3, PieChart as PieChartIcon, Filter, Layers3, Loader2 } from "lucide-react";

const PERIOD_OPTIONS = [7, 30, 90];
const OTHER_SEGMENT_ID = "__other__";
const OTHER_SEGMENT_COLOR = "#64748b";

const DIMENSION_OPTIONS = [
  {
    key: "systems",
    label: "AI Systems",
    description: "Compare daily credit usage by AI system.",
    available: true,
  },
  {
    key: "assistants",
    label: "Agents",
    description: "Compare daily credit usage by agent.",
    available: true,
  },
  {
    key: "accounts",
    label: "Accounts",
    description: "Requires dedicated account attribution before charts can be enabled.",
    available: false,
  },
];

export default function CreditUsageShell() {
  const [periodDays, setPeriodDays] = useState(30);
  const [dimension, setDimension] = useState("systems");
  const [usageAnalytics, setUsageAnalytics] = useState(null);
  const [selectedEntityIds, setSelectedEntityIds] = useState([]);
  const [selectedDrilldownEntityId, setSelectedDrilldownEntityId] = useState("");
  const [usageDrilldown, setUsageDrilldown] = useState(null);
  const [drilldownLoading, setDrilldownLoading] = useState(false);
  const [drilldownError, setDrilldownError] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const activeDimension = useMemo(
    () => DIMENSION_OPTIONS.find((option) => option.key === dimension) || DIMENSION_OPTIONS[0],
    [dimension]
  );

  useEffect(() => {
    let isActive = true;

    const loadUsageAnalytics = async () => {
      setLoading(true);
      setError("");

      try {
        const data = await getAIUsageAnalytics({
          period_days: periodDays,
          dimension,
        });

        if (!isActive) {
          return;
        }

        setUsageAnalytics(data);
      } catch (err) {
        if (!isActive) {
          return;
        }

        setUsageAnalytics(null);
        setError(err.message || "Failed to load usage analytics.");
      } finally {
        if (isActive) {
          setLoading(false);
        }
      }
    };

    loadUsageAnalytics();

    return () => {
      isActive = false;
    };
  }, [periodDays, dimension]);

  useEffect(() => {
    if (!usageAnalytics) {
      return;
    }

    const availableIds = usageAnalytics.entities.map((entity) => entity.entity_id);
    setSelectedEntityIds((currentSelectedIds) => {
      const preservedIds = currentSelectedIds.filter((entityId) => availableIds.includes(entityId));
      return preservedIds.length > 0 ? preservedIds : availableIds;
    });
  }, [usageAnalytics]);

  useEffect(() => {
    if (dimension !== "systems" || !usageAnalytics?.capabilities?.supports_drilldown) {
      setSelectedDrilldownEntityId("");
      setUsageDrilldown(null);
      setDrilldownError("");
      setDrilldownLoading(false);
      return;
    }

    const availableIds = usageAnalytics.entities.map((entity) => entity.entity_id);
    setSelectedDrilldownEntityId((currentSelectedId) =>
      availableIds.includes(currentSelectedId) ? currentSelectedId : (availableIds[0] || "")
    );
  }, [dimension, usageAnalytics]);

  useEffect(() => {
    if (
      dimension !== "systems"
      || !usageAnalytics?.capabilities?.supports_drilldown
      || !selectedDrilldownEntityId
    ) {
      return;
    }

    let isActive = true;

    const loadUsageDrilldown = async () => {
      setDrilldownLoading(true);
      setDrilldownError("");

      try {
        const data = await getAIUsageDrilldown({
          period_days: periodDays,
          parent_dimension: "systems",
          parent_id: selectedDrilldownEntityId,
          breakdown: "experts",
        });

        if (!isActive) {
          return;
        }

        setUsageDrilldown(data);
      } catch (err) {
        if (!isActive) {
          return;
        }

        setUsageDrilldown(null);
        setDrilldownError(err.message || "Failed to load drill-down data.");
      } finally {
        if (isActive) {
          setDrilldownLoading(false);
        }
      }
    };

    loadUsageDrilldown();

    return () => {
      isActive = false;
    };
  }, [dimension, periodDays, selectedDrilldownEntityId, usageAnalytics]);

  const entityColorMap = useMemo(() => {
    const colorMap = {};

    (usageAnalytics?.entities || []).forEach((entity) => {
      colorMap[entity.entity_id] = entity.color;
    });

    colorMap[OTHER_SEGMENT_ID] = OTHER_SEGMENT_COLOR;
    return colorMap;
  }, [usageAnalytics]);

  const filteredEntities = useMemo(
    () => (usageAnalytics?.entities || []).filter((entity) => selectedEntityIds.includes(entity.entity_id)),
    [usageAnalytics, selectedEntityIds]
  );

  const chartData = useMemo(() => {
    if (!usageAnalytics) {
      return [];
    }

    return usageAnalytics.series.map((bucket) => {
      const row = {
        bucketLabel: bucket.bucket_label,
        totalCreditsUsed: bucket.total_credits_used,
      };
      let hiddenSelectedUsage = 0;

      bucket.segments.forEach((segment) => {
        if (selectedEntityIds.includes(segment.entity_id)) {
          row[segment.entity_id] = segment.credits_used;
          return;
        }

        hiddenSelectedUsage += segment.credits_used;
      });

      const otherVisibleUsage = hiddenSelectedUsage + bucket.other_credits_used + bucket.unassigned_credits_used;
      if (otherVisibleUsage > 0) {
        row[OTHER_SEGMENT_ID] = Number(otherVisibleUsage.toFixed(4));
      }

      return row;
    });
  }, [usageAnalytics, selectedEntityIds]);

  const hasOtherSeries = useMemo(
    () => chartData.some((bucket) => Number(bucket[OTHER_SEGMENT_ID] || 0) > 0),
    [chartData]
  );

  const distributionData = useMemo(() => {
    if (!usageAnalytics) {
      return [];
    }

    const visibleSlices = usageAnalytics.distribution.filter(
      (slice) => slice.entity_id === OTHER_SEGMENT_ID || selectedEntityIds.includes(slice.entity_id)
    );
    const totalVisibleCredits = visibleSlices.reduce(
      (sum, slice) => sum + Number(slice.credits_used || 0),
      0
    );

    return visibleSlices.map((slice) => ({
      ...slice,
      share_percent: totalVisibleCredits > 0
        ? Number(((slice.credits_used / totalVisibleCredits) * 100).toFixed(2))
        : 0,
    }));
  }, [usageAnalytics, selectedEntityIds]);

  const visibleTotalCredits = useMemo(
    () => distributionData.reduce((sum, slice) => sum + Number(slice.credits_used || 0), 0),
    [distributionData]
  );

  const hasUsageData = Number(usageAnalytics?.summary?.total_credits_used || 0) > 0;
  const selectedDrilldownEntity = useMemo(
    () => usageAnalytics?.entities?.find((entity) => entity.entity_id === selectedDrilldownEntityId) || null,
    [selectedDrilldownEntityId, usageAnalytics]
  );

  const toggleEntity = (entityId) => {
    setSelectedEntityIds((currentSelectedIds) =>
      currentSelectedIds.includes(entityId)
        ? currentSelectedIds.filter((selectedId) => selectedId !== entityId)
        : [...currentSelectedIds, entityId]
    );
  };

  const formatCredits = (value) =>
    Number(value || 0).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  return (
    <div className="space-y-7">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <h2 className="text-lg font-semibold text-textPrimary">Usage</h2>
          <p className="text-sm text-textSecondary mt-1 max-w-3xl">
            Analyze how credits were consumed over time, compare systems or agents,
            and drill into the contributors behind the usage.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Badge variant="info">Phase 1: Systems and Agents</Badge>
          <Badge variant="default">Accounts Planned</Badge>
        </div>
      </div>

      <Card className="border-primary/20 bg-primary/5">
        <CardContent className="p-6 space-y-6 sm:p-7">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
            <div>
              <h3 className="text-xl font-semibold text-textPrimary">
                Credit usage analytics
              </h3>
              <p className="text-sm text-textSecondary mt-2 max-w-3xl">
                Compare how credits were consumed over the selected period, then filter systems or agents in and out of the charts.
              </p>
            </div>
            {usageAnalytics && (
              <Badge variant="success">
                {usageAnalytics.summary.active_entity_count} contributors in range
              </Badge>
            )}
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
            <div>
              <p className="text-xs uppercase tracking-wider text-textSecondary">Period</p>
              <div className="flex flex-wrap gap-2 mt-2">
                {PERIOD_OPTIONS.map((option) => {
                  const isActive = periodDays === option;

                  return (
                    <Button
                      key={option}
                      variant={isActive ? "default" : "outline"}
                      size="sm"
                      className="rounded-full"
                      onClick={() => setPeriodDays(option)}
                    >
                      Last {option} days
                    </Button>
                  );
                })}
              </div>
            </div>

            <div>
              <p className="text-xs uppercase tracking-wider text-textSecondary">Break down by</p>
              <div className="flex flex-wrap gap-2 mt-2">
                {DIMENSION_OPTIONS.map((option) => {
                  const isActive = dimension === option.key;

                  return (
                    <Button
                      key={option.key}
                      variant={isActive ? "default" : "outline"}
                      size="sm"
                      className="rounded-full"
                      disabled={!option.available}
                      onClick={() => setDimension(option.key)}
                      title={option.description}
                    >
                      {option.label}
                    </Button>
                  );
                })}
              </div>
              <p className="text-sm text-textSecondary mt-2">{activeDimension.description}</p>
            </div>
          </div>

          {usageAnalytics && usageAnalytics.entities.length > 0 && (
            <div>
              <div className="flex items-center justify-between gap-3 mb-2">
                <p className="text-xs uppercase tracking-wider text-textSecondary flex items-center gap-1.5">
                  <Filter className="w-3.5 h-3.5" />
                  Contributor Filters
                </p>
                {selectedEntityIds.length !== usageAnalytics.entities.length && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setSelectedEntityIds(usageAnalytics.entities.map((entity) => entity.entity_id))}
                  >
                    Select all
                  </Button>
                )}
              </div>

              <div className="flex flex-wrap gap-2">
                {usageAnalytics.entities.map((entity) => {
                  const isActive = selectedEntityIds.includes(entity.entity_id);

                  return (
                    <Button
                      key={entity.entity_id}
                      variant={isActive ? "default" : "outline"}
                      size="sm"
                      className="rounded-full"
                      onClick={() => toggleEntity(entity.entity_id)}
                    >
                      <span
                        className="inline-block w-2.5 h-2.5 rounded-full mr-2"
                        style={{ backgroundColor: entity.color }}
                      />
                      {entity.name}
                    </Button>
                  );
                })}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {loading && (
        <Card>
          <CardContent className="px-8 py-10 sm:px-10 sm:py-12 flex items-center justify-center gap-2 text-textSecondary">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading usage analytics...
          </CardContent>
        </Card>
      )}

      {!loading && error && (
        <Card>
          <CardContent className="px-6 py-5 text-sm text-destructive sm:px-7 sm:py-6">
            {error}
          </CardContent>
        </Card>
      )}

      {!loading && !error && usageAnalytics && (
        <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
          <Card>
            <CardContent className="px-6 py-5 sm:px-7 sm:py-6">
              <p className="text-xs uppercase tracking-wider text-textSecondary">Total Credits Used</p>
              <p className="text-2xl font-semibold text-textPrimary mt-2">
                {formatCredits(usageAnalytics.summary.total_credits_used)}
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="px-6 py-5 sm:px-7 sm:py-6">
              <p className="text-xs uppercase tracking-wider text-textSecondary">Average Per Day</p>
              <p className="text-2xl font-semibold text-textPrimary mt-2">
                {formatCredits(usageAnalytics.summary.average_daily_credits_used)}
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="px-6 py-5 sm:px-7 sm:py-6">
              <p className="text-xs uppercase tracking-wider text-textSecondary">Run Count</p>
              <p className="text-2xl font-semibold text-textPrimary mt-2">
                {Number(usageAnalytics.summary.run_count || 0).toLocaleString()}
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-[minmax(0,1.7fr)_minmax(320px,1fr)]">
        <Card>
          <CardContent className="p-6 sm:p-7">
            <div className="flex items-start justify-between gap-3 mb-4">
              <div>
                <h3 className="text-sm font-semibold text-textSecondary uppercase tracking-wider">
                  Usage Over Time
                </h3>
                <p className="text-sm text-textPrimary mt-2">
                  Daily stacked bars for the last {periodDays} days, segmented by {activeDimension.label.toLowerCase()}.
                </p>
              </div>
              <BarChart3 className="w-5 h-5 text-primary" />
            </div>

            {!loading && !error && usageAnalytics && hasUsageData && chartData.length > 0 ? (
              <div className="h-[320px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartData} margin={{ top: 8, right: 12, left: 0, bottom: 8 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(148, 163, 184, 0.18)" />
                    <XAxis dataKey="bucketLabel" stroke="currentColor" tick={{ fill: "var(--color-text-secondary)", fontSize: 12 }} />
                    <YAxis
                      stroke="currentColor"
                      tick={{ fill: "var(--color-text-secondary)", fontSize: 12 }}
                      tickFormatter={(value) => formatCredits(value)}
                    />
                    <RechartsTooltip
                      formatter={(value, name) => {
                        const label = name === OTHER_SEGMENT_ID
                          ? "Other"
                          : usageAnalytics.entities.find((entity) => entity.entity_id === name)?.name || name;
                        return [`${formatCredits(value)} credits`, label];
                      }}
                      labelFormatter={(label) => `Date: ${label}`}
                    />
                    {filteredEntities.map((entity) => (
                      <Bar
                        key={entity.entity_id}
                        dataKey={entity.entity_id}
                        stackId="credits"
                        fill={entity.color}
                        radius={[4, 4, 0, 0]}
                      />
                    ))}
                    {hasOtherSeries && (
                      <Bar
                        dataKey={OTHER_SEGMENT_ID}
                        stackId="credits"
                        fill={OTHER_SEGMENT_COLOR}
                        radius={[4, 4, 0, 0]}
                      />
                    )}
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="rounded-xl border border-dashed border-borderDefault bg-backgroundSurface/50 min-h-[300px] px-8 py-10 sm:px-10 sm:py-12 flex items-center justify-center text-center">
                <div className="max-w-xl space-y-3">
                  <p className="text-base font-medium text-textPrimary">
                    {selectedEntityIds.length === 0 ? "Select at least one contributor" : "No usage data yet"}
                  </p>
                  <p className="text-sm text-textSecondary">
                    {selectedEntityIds.length === 0
                      ? "Use the contributor filters above to include at least one system or agent in the chart."
                      : `This chart will populate once ${activeDimension.label.toLowerCase()} have consumed credits in the selected period.`}
                  </p>
                  <div className="flex flex-wrap items-center justify-center gap-3 text-xs text-textSecondary uppercase tracking-wider pt-2">
                    <span className="inline-flex items-center gap-1.5">
                      <Layers3 className="w-3.5 h-3.5" />
                      Stacked by entity
                    </span>
                    <span className="inline-flex items-center gap-1.5">
                      <Filter className="w-3.5 h-3.5" />
                      Filterable contributors
                    </span>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6 sm:p-7">
            <div className="flex items-start justify-between gap-3 mb-4">
              <div>
                <h3 className="text-sm font-semibold text-textSecondary uppercase tracking-wider">
                  Cost Distribution
                </h3>
                <p className="text-sm text-textPrimary mt-2">
                  Share of total credits used by the selected {activeDimension.label.toLowerCase()} in the same period.
                </p>
              </div>
              <PieChartIcon className="w-5 h-5 text-primary" />
            </div>

            {!loading && !error && usageAnalytics && distributionData.length > 0 && visibleTotalCredits > 0 ? (
              <div className="space-y-4">
                <div className="h-[260px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={distributionData}
                        dataKey="credits_used"
                        nameKey="name"
                        innerRadius={70}
                        outerRadius={100}
                        paddingAngle={2}
                      >
                        {distributionData.map((slice) => (
                          <Cell
                            key={slice.entity_id}
                            fill={entityColorMap[slice.entity_id] || OTHER_SEGMENT_COLOR}
                          />
                        ))}
                      </Pie>
                      <RechartsTooltip
                        formatter={(value, _, item) => [
                          `${formatCredits(value)} credits`,
                          item?.payload?.name || "Contributor",
                        ]}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>

                <div className="space-y-2">
                  {distributionData.map((slice) => (
                    <div
                      key={slice.entity_id}
                      className="flex items-center justify-between gap-3 rounded-lg border border-borderDefault px-4 py-3 text-sm"
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        <span
                          className="inline-block w-2.5 h-2.5 rounded-full shrink-0"
                          style={{ backgroundColor: entityColorMap[slice.entity_id] || OTHER_SEGMENT_COLOR }}
                        />
                        <span className="text-textPrimary truncate">{slice.name}</span>
                      </div>
                      <div className="text-right shrink-0">
                        <div className="font-medium text-textPrimary">{formatCredits(slice.credits_used)}</div>
                        <div className="text-textSecondary text-xs">{slice.share_percent.toFixed(1)}%</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="rounded-xl border border-dashed border-borderDefault bg-backgroundSurface/50 min-h-[300px] px-8 py-10 sm:px-10 sm:py-12 flex items-center justify-center text-center">
                <div className="max-w-sm space-y-3">
                  <p className="text-base font-medium text-textPrimary">
                    Distribution unavailable
                  </p>
                  <p className="text-sm text-textSecondary">
                    The distribution chart will appear once the selected {activeDimension.label.toLowerCase()} have consumed credits in the chosen period.
                  </p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardContent className="p-6 space-y-5 sm:p-7">
          <div className="flex flex-col gap-2 lg:flex-row lg:items-start lg:justify-between">
            <div>
              <h3 className="text-sm font-semibold text-textSecondary uppercase tracking-wider">
                Top Contributors
              </h3>
              <p className="text-sm text-textPrimary mt-2">
                {dimension === "systems"
                  ? "Use this table to compare which systems are consuming the most credits before drilling down further."
                  : "Use this table to compare agent credit consumption and run volume for the same period."}
              </p>
            </div>
            {dimension === "systems" ? (
              <Badge variant="success">Phase 1 ready</Badge>
            ) : (
              <Badge variant="info">Agent analytics live</Badge>
            )}
          </div>

          <div className="rounded-xl border border-borderDefault overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-borderDefault text-xs uppercase tracking-wider text-textSecondary">
                    <th className="text-left px-5 py-3.5">{dimension === "systems" ? "AI System" : "Agent"}</th>
                    <th className="text-right px-5 py-3.5">Credits Used</th>
                    <th className="text-right px-5 py-3.5">Runs</th>
                    <th className="text-right px-5 py-3.5">Avg / Run</th>
                    <th className="text-right px-5 py-3.5">Share</th>
                  </tr>
                </thead>
                <tbody>
                  {usageAnalytics?.entities?.length > 0 ? (
                    usageAnalytics.entities.map((entity) => (
                      <tr
                        key={entity.entity_id}
                        className={`border-b border-borderDefault/50 hover:bg-secondary/5 ${selectedDrilldownEntityId === entity.entity_id ? "bg-primary/5" : ""}`}
                      >
                        <td className="px-5 py-3.5">
                          <button
                            type="button"
                            className="flex items-center gap-2 min-w-0 text-left w-full"
                            onClick={() => setSelectedDrilldownEntityId(entity.entity_id)}
                          >
                            <span
                              className="inline-block w-2.5 h-2.5 rounded-full shrink-0"
                              style={{ backgroundColor: entity.color }}
                            />
                            <span className="text-textPrimary truncate">{entity.name}</span>
                          </button>
                        </td>
                        <td className="px-5 py-3.5 text-right font-medium text-textPrimary">
                          {formatCredits(entity.credits_used)}
                        </td>
                        <td className="px-5 py-3.5 text-right text-textPrimary">
                          {Number(entity.run_count || 0).toLocaleString()}
                        </td>
                        <td className="px-5 py-3.5 text-right text-textPrimary">
                          {formatCredits(entity.average_credits_per_run)}
                        </td>
                        <td className="px-5 py-3.5 text-right text-textPrimary">
                          {Number(entity.share_percent || 0).toFixed(1)}%
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={5} className="px-6 py-10 text-center text-textSecondary">
                        No usage contributors were recorded in this period yet.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {dimension === "systems" && (
            <div className="rounded-xl border border-borderDefault bg-backgroundSurface/40 p-5 space-y-5 sm:p-6">
              <div className="flex flex-col gap-2 lg:flex-row lg:items-start lg:justify-between">
                <div>
                  <h4 className="text-sm font-semibold text-textSecondary uppercase tracking-wider">
                    Expert Breakdown
                  </h4>
                  <p className="text-sm text-textPrimary mt-2">
                    {selectedDrilldownEntity
                      ? `See which experts are driving credit usage inside ${selectedDrilldownEntity.name}.`
                      : "Select a system above to load its expert-level usage breakdown."}
                  </p>
                </div>
                {usageDrilldown && (
                  <Badge variant="outline">
                    {Number(usageDrilldown.summary.run_count || 0).toLocaleString()} expert runs
                  </Badge>
                )}
              </div>

              {drilldownLoading ? (
                <div className="rounded-lg border border-dashed border-borderDefault px-6 py-10 text-sm text-textSecondary text-center sm:px-8 sm:py-12">
                  Loading expert breakdown...
                </div>
              ) : drilldownError ? (
                <div className="rounded-lg border border-dashed border-destructive/30 px-6 py-8 text-sm text-destructive text-center sm:px-8 sm:py-10">
                  {drilldownError}
                </div>
              ) : usageDrilldown?.rows?.length > 0 ? (
                <div className="rounded-lg border border-borderDefault overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-borderDefault text-xs uppercase tracking-wider text-textSecondary">
                          <th className="text-left px-5 py-3.5">Expert</th>
                          <th className="text-right px-5 py-3.5">Credits Used</th>
                          <th className="text-right px-5 py-3.5">Model</th>
                          <th className="text-right px-5 py-3.5">Compute</th>
                          <th className="text-right px-5 py-3.5">Runs</th>
                          <th className="text-right px-5 py-3.5">Avg / Run</th>
                          <th className="text-right px-5 py-3.5">Share</th>
                        </tr>
                      </thead>
                      <tbody>
                        {usageDrilldown.rows.map((row) => (
                          <tr key={row.expert_id} className="border-b border-borderDefault/50 hover:bg-secondary/5">
                            <td className="px-5 py-3.5 text-textPrimary">{row.expert_name}</td>
                            <td className="px-5 py-3.5 text-right font-medium text-textPrimary">
                              {formatCredits(row.credits_used)}
                            </td>
                            <td className="px-5 py-3.5 text-right text-textPrimary">
                              {formatCredits(row.model_credits_used)}
                            </td>
                            <td className="px-5 py-3.5 text-right text-textPrimary">
                              {formatCredits(row.compute_credits_used)}
                            </td>
                            <td className="px-5 py-3.5 text-right text-textPrimary">
                              {Number(row.run_count || 0).toLocaleString()}
                            </td>
                            <td className="px-5 py-3.5 text-right text-textPrimary">
                              {formatCredits(row.average_credits_per_run)}
                            </td>
                            <td className="px-5 py-3.5 text-right text-textPrimary">
                              {Number(row.share_percent || 0).toFixed(1)}%
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : (
                <div className="rounded-lg border border-dashed border-borderDefault px-6 py-10 text-sm text-textSecondary text-center sm:px-8 sm:py-12">
                  No expert usage rows were recorded for this system in the selected period.
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
